
import { Component, Input, ChangeDetectorRef, OnChanges, AfterViewInit, ElementRef, Output, EventEmitter, OnDestroy, signal, WritableSignal, inject } from '@angular/core';
import { MediaInfoResponse } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';
import { TranslateService } from '@ngx-translate/core';
import { getDateFromNow } from '../../admin/entities/utils';
import { Router } from '@angular/router';
import { MetricsService } from 'app/services';

@Component({
    selector: 'key-video',
    templateUrl: './video.component.html',
    styleUrls: ['video.component.scss'],

})
export class VideoComponent implements OnChanges, AfterViewInit, OnDestroy {

    @Input() asset: string;
    @Input() video: { url: string, filename: string, type: string, isDeleted: boolean };
    @Input() muted = true;
    @Input() controls = false;
    @Input() download = true;
    @Input() cover = false;

    @Output() onLoaded = new EventEmitter<VideoComponent>();
    @Output() onStatusChanged = new EventEmitter<VideoComponent>();
    @Output() onProgress = new EventEmitter<VideoComponent>();
    @Output() infoUpdated = new EventEmitter<MediaInfoResponse>();

    loaded: WritableSignal<boolean> = signal(false);
    status: WritableSignal<MediaInfoResponse> = signal(null);
    requesterName: string;
    requesterDate: string;
    requesterType: string;
    changedByUser: string;
    error: WritableSignal<string> = signal('');
    videoElement: HTMLVideoElement;
    isPlaying = false;
    progress = 0;
    currentTime: string;
    durationTime: string;
    timer: any;

    private currentVideoFilename = '';

    get duration(): number {
        return this.videoElement.duration;
    }

    private router = inject(Router);
    private metrics = inject(MetricsService);

    constructor(
        private el: ElementRef,
        private app: AppService,
        private i18l: TranslateService,
        private ref: ChangeDetectorRef
    ) {
    }

    ngOnChanges() {
        if (this.video.filename !== this.currentVideoFilename) {
            this.status.set(null);
            this.currentVideoFilename = this.video.filename;
        }

        // we check the video status is not failed, otherwise the videoElement would keep looping round
        if (this.videoElement && this.status()?.status !== 'failed') {
            this.status.set(null);
            this.videoElement.load(); // force any changes to the video element to load
        }
    }

    ngOnDestroy() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    setupVideoElement() {
        this.videoElement = this.el.nativeElement.querySelectorAll('video')[0];
        this.videoElement.controls = this.controls;

        if (this.videoElement) {

            this.videoElement.onplaying = () => {
                this.isPlaying = true;
                this.onStatusChanged.emit(this);
            };

            this.videoElement.onpause = () => {
                this.isPlaying = false;
                this.onStatusChanged.emit(this);
            };

            this.videoElement.onended = () => {
                this.isPlaying = false;
                this.onStatusChanged.emit(this);
            };

            this.videoElement.ontimeupdate = () => {
                this.progress = 100 * this.videoElement.currentTime / this.videoElement.duration;
                this.currentTime = this.formatTime(Math.ceil(this.videoElement.currentTime));
                this.durationTime = this.formatTime(Math.ceil(this.videoElement.duration));
                this.onProgress.emit(this);
            };

            this.videoElement.addEventListener('error', this.handleMediaError.bind(this), true);
            this.videoElement.onerror = this.handleMediaError.bind(this);

            this.videoElement.onloadeddata = () => {
                // videos must be muted on iOS in order to play multiple videos. on all other OS's we will play the sound of the first video only to avoid a mishmash of sounds.
                // NOTE: the muted attr can't be assigned in the template because of the dynamic nature of angular templates... some further reading on the subject: https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
                // this.videoElement.muted = this.matchMedia.deviceFeatures.os === DeviceFeaturesOs.IOS || index > 0;
                this.videoElement.muted = this.muted;
                this.loaded.set(true);
                this.onLoaded.emit(this);

                // if a user requests a video that is readily available e.g it has previously been requested+loaded status: 'ready'
                // we need to update the video details in the parent to reflect this as 'handleMediaError()' will not be reached
                this.handlePreviouslyRequestedLoadedVideo();
                this.ref.markForCheck();
            };

        }
        this.ref.markForCheck();
    }

    ngAfterViewInit() {
        this.setupVideoElement();
    }

    async handlePreviouslyRequestedLoadedVideo() {
        const info = await this.app.api.media.getMediaInfo(this.asset, this.video.filename);
        this.infoUpdated.emit(info);
    }

    handleMediaError(): boolean {
        this.loaded.set(true);
        this.onLoaded.emit(this);

        if (this.video) {

            const updateMediaStatus = async () => {

                try {
                    const info = await this.app.api.media.getMediaInfo(this.asset, this.video.filename);
                    // we do a filename check to cover the scenario where the user has slow internet and switches between 2 videos in 2 different states quickly i.e. failed and ready
                    if (info.status !== 'ready' && info.filename === this.currentVideoFilename) {
                        this.status.set(info);
                        const by = this.status().status && this.status().status + 'By';
                        this.changedByUser = ['deleted', 'canceled'].includes(this.status()?.status) ? this.status()?.data[by].name : '';
                        if (info.requester) {
                            this.requesterName = info.requester.type !== 'device' ? info.requester.name : '';
                            this.requesterType = this.i18l.instant('ADMIN.ENTITIES.' + info.requester.type.toUpperCase()).toLowerCase();
                            this.requesterDate = getDateFromNow(info.entity.creationDate);
                        }
                        this.error.set(null);

                        if (info.status !== 'canceled') {
                            // We don't need to retry if the request failed...
                            this.timer = setTimeout(() => updateMediaStatus(), 10e3); // update again in a little bit
                        }

                        if (info.status === 'failed') {
                            clearTimeout(this.timer);
                        }
                    } else {
                        this.status.set(null);
                        this.error.set(null);

                        // this forces the video to get recreated and play, gonna need some more testing with dual camera systems
                        this.video = {
                            ...this.video,
                        };
                        setTimeout(() => this.ngAfterViewInit(), 100);
                    }
                    this.infoUpdated.emit(info);
                } catch (err) {
                    this.status.set(null);
                    if (err.name === 'ForbiddenError') {
                        this.error.set(this.i18l.instant('MEDIA.ERRORS.FORBIDDEN'));
                    } else {
                        this.error.set(this.i18l.instant('MEDIA.ERRORS.GENERIC'));
                    }
                } finally {
                    this.ref.markForCheck();
                }
            };
            updateMediaStatus();
            return true;

        }

        this.error.set(this.i18l.instant('MEDIA.ERRORS.GENERIC'));
        return false;

    }

    formatTime(time: number) {
        time = Math.ceil(time);
        const seconds = time % 60;
        const minutes = Math.floor(time / 60 % 60);
        function addZero(number) {
            return number.toString().length > 1 ? number : '0' + number;
        }
        return addZero(minutes) + ':' + addZero(seconds);
    }

    play() {
        this.videoElement.play();
    }

    pause() {
        this.videoElement.pause();
    }

    seek(type: 'start' | 'end' | 'forward' | 'backward' | 'exact', value?: number) {

        switch (type) {
            case 'start':
                this.videoElement.currentTime = 0;
                break;
            case 'end':
                this.videoElement.currentTime = this.videoElement.duration;
                break;
            case 'forward':
                this.videoElement.currentTime += value;
                break;
            case 'backward':
                this.videoElement.currentTime -= value;
                break;
            case 'exact':
                this.videoElement.currentTime = value;
                break;
        }
    }

    onDownload() {
        this.captureAnalytics();
    }

    private captureAnalytics() {
        //Videos tab on live map.
        const isLiveMap = ['status', 'videos'].every(s => this.router.url.includes(s));

        if (isLiveMap) {
            this.metrics.capture('video_download', {
                screen: 'live_map',
                tab: 'videos',
                source: 'pop_up',
                device_id: ''
            });
        }
    }

}


